import type { AssistantPageKind } from '@/services/api/domains/chatbot.api'

/**
 * Ordered prefix → AssistantPageKind map. Deliberately NOT sourced from
 * `routes/types.ts` (ROUTES) — a route→capability manifest (Phase 2a) is the
 * intended future source of truth; this hand-maintained prefix table is the
 * correct and cheap stand-in until then (task-3-brief.md).
 *
 * Order matters: entries are matched top-to-bottom and the first match wins.
 * All prefixes here happen to be distinct first path segments, so no entry
 * can currently shadow another — but `routeToPageKind` matches on a
 * segment boundary (exact path or `${prefix}/...`) rather than a bare
 * `startsWith`, specifically so a route that merely shares a prefix STRING
 * with one of these (e.g. a hypothetical `/mapx`) never gets misclassified.
 * Keep more specific prefixes earlier if a future addition could otherwise
 * be shadowed.
 */
const PAGE_KIND_PREFIXES: ReadonlyArray<readonly [string, AssistantPageKind]> = [
  ['/dashboard', 'dashboard'],
  ['/map', 'map'],
  ['/library', 'library'],
  ['/forms', 'forms'],
  ['/scripts', 'scripts'],
  ['/security', 'security'],
  ['/monitoring', 'monitoring'],
  ['/capacity', 'capacity'],
  ['/settings', 'settings'],
  ['/geocoding', 'geocoding'],
]

/**
 * Maps a route pathname to the closed `AssistantPageKind` union the backend
 * accepts (a Zod enum — an unknown string is rejected). Falls back to
 * `'other'` for anything unrecognized; NEVER return a value outside the
 * union.
 */
export function routeToPageKind(pathname: string): AssistantPageKind {
  if (pathname === '/') return 'home'

  for (const [prefix, kind] of PAGE_KIND_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return kind
  }

  return 'other'
}
